import type { LevelId } from '../data/ids';

export type { LevelId };

/** GDD §6.4: the whole persistent state lives in one versioned JSON document under this key. */
export const SAVE_KEY = 'narcoverse:save';
export const SCHEMA_VERSION = 2 as const;

export const SLOT_IDS = ['EXP-01', 'EXP-02', 'EXP-03'] as const;
export type SlotId = (typeof SLOT_IDS)[number];

export const JUMP_GRADES = ['PERFECTO', 'EXCELENTE', 'BUENO', 'REGULAR', 'FORZOSO'] as const;
export type JumpGrade = (typeof JUMP_GRADES)[number];

/** Level 1 and 2 have no jump (issue #44): `bestJump` is null for them. */
export interface LevelRecord {
  bestScore: number;
  bestJump: JumpGrade | null;
  cleanRun: boolean;
  /** The best score was made with the assist mode on (schema v2, #138). */
  assisted: boolean;
}

export interface SaveSlot {
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  playTimeMs: number;
  unlockedLevels: LevelId[]; // always includes "1-1"
  completedLevels: Partial<Record<LevelId, LevelRecord>>;
  current: {
    levelId: LevelId;
    checkpointId: string | null; // e.g. "CP-102-A"; null = start of the level
    lives: number; // 1..9
    dollars: number; // 0..99, counter towards the next extra life
    score: number;
    scoreAtLevelStart: number; // restored after a game over (§6.2)
    cleanRun: boolean; // no life lost in this attempt, for the clean-flight bonus (§12)
  };
  stats: { deaths: number; flaps: number; bossesDefeated: number; perfectJumps: number };
}

export interface Settings {
  musicVolume: number; // 0..1
  sfxVolume: number; // 0..1
  reducedMotion: boolean;
  assistMode: boolean;
  showFps: boolean;
}

export interface SaveFile {
  schemaVersion: typeof SCHEMA_VERSION;
  lastSlotId: SlotId | null;
  settings: Settings;
  slots: Record<SlotId, SaveSlot | null>;
}

/** Ranges of the schema (GDD §6.4). */
export const SAVE_LIMITS = { livesMin: 1, livesMax: 9, dollarsMax: 99 } as const;
